<div class="server-settings-wrapper">
  <!-- Enhanced Header Section -->
  <div class="template-header">
    <div class="header-content">
      <h1 class="template-title">Serverwide Settings</h1>
      <p class="template-description">
        Configure server-wide settings including roles, date/time formats, and language preferences.
      </p>
    </div>
  </div>

  <!-- Warning Banner -->
  <div *ngIf="guild">
    <div class="warning-banner" *ngIf="!guild.owner">
      <div class="warning-content">
        <mat-icon class="warning-icon">warning</mat-icon>
        <div class="warning-text">
          <strong>Permission Required</strong>
          <p>Only the <a href="https://docs.raidorganizer.org/getting-started/terms#server-owner" target="_blank">server owner</a>,
            <a href="https://docs.raidorganizer.org/getting-started/terms#administrator" target="_blank">administrators</a> or
            <a href="https://docs.raidorganizer.org/getting-started/terms#moderator-role" target="_blank">moderators</a> of the server
            can change these settings!</p>
        </div>
      </div>
    </div>
  </div>

  <ng-container *ngIf="serverInfo$ | async as serverInfo">
    <!-- Role Settings Section -->
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Role Settings</h2>
        <mat-icon class="section-icon">settings</mat-icon>
      </div>
      <div class="row">
        <div class="col">
          <form class="moderatorRoleSelection">
            <mat-form-field class="full-width-container" floatLabel="always">
              <mat-label for="moderatorRoleSelection">Moderator role</mat-label>
              <mat-select
                      [(ngModel)]="serverInfo.moderator_role"
                      name="organizerRole"
                      id="moderatorRoleSelection"
                      [disabled]="!guild.owner"
                      (selectionChange)="setModeratorRole($event)"
                      placeholder="Select a role"
                      >
                <ng-container *ngFor="let role of serverInfo.roles">
                  <mat-option [value]="role.id"
                          [disabled]="role.name === '@everyone'" [selected]="role.id === serverInfo.moderator_role">
                    {{ role.name }}
                  </mat-option>
                </ng-container>
              </mat-select>
              <div class="mat-select-link-wrapper">
                <a href="https://docs.raidorganizer.org/getting-started/terms#moderator-role" target="_blank" class="mat-select-link">What is the Moderator Role?</a>
              </div>
            </mat-form-field>
          </form>
        </div>
      </div>
    </div>

    <!-- Date and Time Settings Section -->
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Date and Time Settings</h2>
        <mat-icon class="section-icon">schedule</mat-icon>
      </div>
      <div class="row">
        <div class="col">
          <form class="dateFormatInput">
            <mat-form-field class="full-width-container">
              <mat-label>Date Format</mat-label>
              <input matInput #dateFormat maxlength="10" placeholder="YYYY-MM-DD" [value]="serverInfo.selected_date_format" [disabled]="!guild.owner" (change)="setDateFormat($event.target)">
              <mat-hint align="end">{{dateFormat.value.length}} / 10</mat-hint>
            </mat-form-field>
          </form>
        </div>

        <div class="col">
          <form class="timeFormatSelection">
            <mat-form-field class="full-width-container" floatLabel="always">
              <mat-label>Time Format</mat-label>
              <mat-select [(ngModel)]="serverInfo.timeformat" name="timeFormat" [disabled]="!guild.owner" (selectionChange)="setTimeFormat($event.value)">
                <mat-option *ngFor="let timeformat of ['12h', '24h']" [value]="timeformat" [selected]="timeformat === serverInfo.timeformat">
                  {{ timeformat }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </form>
        </div>

        <div class="col">
          <form class="timeZoneSelection">
            <mat-form-field class="full-width-container" floatLabel="always">
              <mat-label for="timeZoneSelection">Time zone</mat-label>
              <mat-select
                [(ngModel)]="serverInfo.timezone"
                name="timezone"
                id="timeZoneSelection"
                [disabled]="!guild.owner"
                (selectionChange)="setTimeZone($event.value)"
                placeholder="Select a timezone"
              >
                <ngx-mat-select-search
                  [formControl]="timezoneFilter"
                  placeholderLabel="Search timezone"
                  noEntriesFoundLabel="No timezones found"
                ></ngx-mat-select-search>
                <mat-option *ngFor="let timezone of filteredTimezones" [value]="timezone">
                  {{ timezone }}
                </mat-option>
              </mat-select>
              <a *ngIf="guild.owner" class="form-text mat-select-link" href="javascript: void(0);" (click)="guessTimezone($event)">
                Let me guess
              </a>
            </mat-form-field>
          </form>
        </div>
      </div>
    </div>

    <!-- Language Settings Section -->
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Language Settings</h2>
        <mat-icon class="section-icon">language</mat-icon>
      </div>
      <div class="row">
        <div class="col">
          <form class="LanguageSelection">
            <mat-form-field class="full-width-container" floatLabel="always">
              <mat-label for="LanguageSelection">Language</mat-label>
              <mat-select [(ngModel)]="serverInfo.locale" name="language" id="LanguageSelection" [disabled]="!guild.owner" (selectionChange)="setLanguage($event.value)">
                <mat-option *ngFor="let language of serverInfo.languages" [value]="language" [selected]="language === serverInfo.locale">
                  {{ language }}
                </mat-option>
              </mat-select>
              <div class="mat-select-link-wrapper">
                <a href="https://discord.gg/9JheGp8fVY" target="_blank" class="mat-select-link">
                  Help us translate
                </a>
              </div>
            </mat-form-field>
          </form>
        </div>
      </div>
    </div>

    <!-- Custom Signup Templates Section -->
    <ng-container *ngIf="serverInfo.custom_templates && serverInfo.custom_templates.length > 0">
      <div class="settings-section">
        <div class="section-header">
          <h2 class="section-title">Custom Signup Templates</h2>
          <mat-icon class="section-icon">list_alt</mat-icon>
        </div>
        <div class="row">
          <div class="col">
            <table mat-table [dataSource]="serverInfo.custom_templates" class="mat-elevation-z8">
              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef> Template Name </th>
                <td mat-cell *matCellDef="let template"> {{template.name}} </td>
              </ng-container>

              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef> Delete </th>
                <td mat-cell *matCellDef="let template">
                  <button mat-icon-button (click)="removeTemplate(template.identifier)" [disabled]="!guild.owner">
                    <fa-icon [icon]="faIcons.trash"></fa-icon>
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="['name', 'actions']"></tr>
              <tr mat-row *matRowDef="let row; columns: ['name', 'actions'];"></tr>
            </table>
          </div>
        </div>
      </div>
    </ng-container>
  </ng-container>
</div>
